

import type { Timestamp } from 'firebase/firestore';
import { z } from 'zod';

const planIdValues = ['free', 'basic-monthly', 'basic-semi-annually', 'basic-annually', 'pro-monthly', 'pro-semi-annually', 'pro-annually'] as const;
export type PlanId = (typeof planIdValues)[number];
export const planIdEnum = z.enum(planIdValues);

export const TemplateCardSchema = z.object({
  name: z.string().min(1, "O nome do template é obrigatório."),
  description: z.string().min(1, "A descrição do template é obrigatória."),
  imageUrl: z.string().url("URL da imagem inválida.").optional(),
});
export type TemplateCardData = z.infer<typeof TemplateCardSchema>;


export type ActionButton = {
  text: string;
  link: string;
  backgroundColor?: string;
  textColor?: string;
};

export type Testimonial = {
  id: string;
  photoUrl?: string;
  photoFile?: File;
  name: string;
  title: string;
  text: string
};

export type FAQItem = {
  id:string;
  question: string;
  answer: string
};

export type ClientLogo = {
  id: string;
  logoUrl?: string;
  logoFile?: File;
  altText: string;
};

export type GalleryImage = {
  id: string;
  imageUrl?: string;
  imageFile?: File;
  altText: string;
  imageHint?: string; // Adicionado para a busca de imagens
};

export type SocialLink = {
  id: string;
  type: 'instagram' | 'facebook' | 'linkedin' | 'x' | 'youtube' | 'tiktok' | 'other';
  urlSuffix: string;
};

export type DifferentialItem = {
  id: string;
  icon: string;
  title: string;
  description: string;
};

export type SingleActionButtonKey =
  | 'block3_actionButton'
  | 'block4_actionButton'
  | 'block_diff_actionButton'
  | 'block5_actionButton'
  | 'block7_actionButton'
  | 'block9_actionButton';

export const SiteDataSchemaCore = z.object({
  userId: z.string(),
  subscriptionId: z.string().optional(), // Vincula o site a uma assinatura
  templateId: z.enum(['template1', 'template2', 'template3', 'template4']),
  siteName: z.string().min(1, "Nome do site é obrigatório"),
  subdomain: z.string().min(1, "Subdomínio é obrigatório").regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Subdomínio inválido (letras minúsculas, números, hífens. Sem espaços ou caracteres especiais.)"),
  customDomain: z.string().optional().describe("Domínio personalizado do cliente, ex: meudominio.com.br"),
  faviconUrl: z.string().optional(),
  faviconFile: z.instanceof(File).optional(),
  googleTagManagerId: z.string().optional().describe("ID do contêiner do Google Tag Manager (ex: GTM-XXXXXXX)"),
  googleAnalyticsId: z.string().optional().describe("ID de rastreamento do Google Analytics 4 (ex: G-XXXXXXXXXX)"),
  googleAdsId: z.string().optional().describe("ID de conversão do Google Ads (ex: AW-XXXXXXXXXX)"),
  facebookPixelId: z.string().optional().describe("ID do Pixel do Facebook"),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor primária inválida").default('#29ABE2'),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor secundária inválida").optional().or(z.literal("")),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor de destaque inválida").optional(),
  isPublished: z.boolean().default(false),
  publishedAt: z.custom<Timestamp | Date | string>().optional().nullable(),
  aiCredits: z.number().optional().default(20),
  aiCreditLastReset: z.custom<Timestamp | Date | string>((val) => val instanceof Timestamp || val instanceof Date || typeof val === 'string').optional().nullable(),
  viewCount: z.number().int().optional().default(0),
  lastVisitedAt: z.custom<Timestamp | Date | string>().optional().nullable(),
  suspendedReason: z.string().optional().nullable(), // Adicionado para motivo de suspensão
  companyName: z.string().optional(),
  companyLogoUrl: z.string().optional().or(z.literal("")),
  companyLogoFile: z.instanceof(File).optional(),
  companySummary: z.string().optional(),
  address: z.string().optional(), // Campo unificado do chat
  address_zipCode: z.string().optional(),
  address_street: z.string().optional(),
  address_number: z.string().optional(),
  address_complement: z.string().optional(),
  address_neighborhood: z.string().optional(),
  address_city: z.string().optional(),
  address_state: z.string().optional(),
  contactEmail: z.string().email("Email de contato inválido").min(1, "Email de contato é obrigatório"),
  phoneDDI: z.string().optional().default('+55'),
  phone: z.string().optional(),
  whatsappDDI: z.string().optional().default('+55'),
  whatsapp: z.string().optional(),
  socialLinks: z.array(z.object({
    id: z.string(),
    type: z.enum(['instagram', 'facebook', 'linkedin', 'x', 'youtube', 'tiktok', 'other']),
    urlSuffix: z.string(),
  })).optional().default([]),
  block_footer_logoUrl: z.string().optional().or(z.literal("")),
  block_footer_logoFile: z.instanceof(File).optional(),

  block2_isActive: z.boolean().optional().default(true),
  block2_order: z.number().optional().default(1),
  block2_backgroundImageUrl: z.string().optional().or(z.literal("")),
  block2_backgroundImageFile: z.instanceof(File).optional(),
  block2_backgroundImage_ai_hint: z.string().optional().default("modern business office"),
  block2_overlayColor: z.string().optional(),
  block2_title: z.string().default(''),
  block2_subtitle: z.string().optional(),
  block2_buttons: z.array(z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  })).default([{ text: 'Saiba Mais', link: '#servicos' }, { text: 'Entre em Contato', link: '#contato' }]),

  block3_isActive: z.boolean().optional().default(true),
  block3_order: z.number().optional().default(2),
  block3_title: z.string().default('Sobre Nossa Empresa'),
  block3_text: z.string().default('<p>Somos dedicados a fornecer o melhor serviço da indústria. Nossa paixão é ajudar você a ter sucesso.</p>'),
  block3_photoUrl: z.string().optional().or(z.literal("")),
  block3_photoFile: z.instanceof(File).optional(),
  block3_photo_ai_hint: z.string().optional().default("team collaboration meeting"),
  block3_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).optional(),

  block4_isActive: z.boolean().optional().default(true),
  block4_order: z.number().optional().default(3),
  block4_title: z.string().default('Nossos Serviços'),
  block4_text: z.string().default('<p>Descubra a gama de serviços que oferecemos para ajudar seu negócio a crescer e prosperar.</p>'),
  block4_photoUrl: z.string().optional().or(z.literal("")),
  block4_photoFile: z.instanceof(File).optional(),
  block4_photo_ai_hint: z.string().optional().default("services"),
  block4_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).optional(),

  block_diff_isActive: z.boolean().optional().default(true),
  block_diff_order: z.number().optional().default(4),
  block_diff_title: z.string().optional().default('Nossos Diferenciais'),
  block_diff_subtitle: z.string().optional(),
  block_diff_items: z.array(z.object({
    id: z.string(),
    icon: z.string(),
    title: z.string(),
    description: z.string(),
  })).optional().default([]),
  block_diff_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).optional(),

  block5_isActive: z.boolean().optional().default(true),
  block5_order: z.number().optional().default(5),
  block5_title: z.string().optional().default('O Que Nossos Clientes Dizem'),
  block5_subtitle: z.string().optional(),
  block5_testimonials: z.array(z.object({
    id: z.string(),
    photoUrl: z.string().optional().or(z.literal("")),
    photoFile: z.instanceof(File).optional(),
    name: z.string(),
    title: z.string(),
    text: z.string(),
  })).optional().default([]),
  block5_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).optional(),

  block6_isActive: z.boolean().optional().default(true),
  block6_order: z.number().optional().default(6),
  block6_title: z.string().optional().default('Clientes que Confiam em Nós'),
  block6_clientLogos: z.array(z.object({
    id: z.string(),
    logoUrl: z.string().optional().or(z.literal("")),
    logoFile: z.instanceof(File).optional(),
    altText: z.string(),
  })).optional().default([]),

  block7_isActive: z.boolean().optional().default(true),
  block7_order: z.number().optional().default(7),
  block7_title: z.string().optional().default('Nosso Portfólio'),
  block7_subtitle: z.string().optional(),
  block7_galleryImages: z.array(z.object({
    id: z.string(),
    imageUrl: z.string().optional().or(z.literal("")),
    imageFile: z.instanceof(File).optional(),
    altText: z.string(),
    imageHint: z.string().optional(),
  })).optional().default([]),
  block7_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).optional(),

  block8_isActive: z.boolean().optional().default(true),
  block8_order: z.number().optional().default(8),
  block8_title: z.string().optional().default('Perguntas Frequentes'),
  block8_subtitle: z.string().optional(),
  block8_faqs: z.array(z.object({
    id: z.string(),
    question: z.string(),
    answer: z.string(),
  })).optional().default([]),

  block10_isActive: z.boolean().optional().default(true),
  block10_order: z.number().optional().default(9),
  block10_title: z.string().optional().default('Visite-nos ou Entre em Contato'),
  block10_column1_isActive: z.boolean().optional().default(true),
  block10_column1_text: z.string().optional().default(
    '<p><strong>Nosso Endereço:</strong><br/>Rua Exemplo, 123, Exemplo Bairro<br/>Cidade Exemplo - UF, 00000-000</p><p><strong>Horário de Funcionamento:</strong><br/>Segunda a Sexta: 09:00 - 18:00<br/>Sábado: 09:00 - 12:00<br/>Domingo: Fechado</p>'
  ),
  block10_column2_map_isActive: z.boolean().optional().default(true),
  block10_column3_form_isActive: z.boolean().optional().default(true),
  block10_column3_formTitle: z.string().optional().default('Fale Conosco'),

  block9_isActive: z.boolean().optional().default(true),
  block9_order: z.number().optional().default(10),
  block9_backgroundImageUrl: z.string().optional().or(z.literal("")),
  block9_backgroundImageFile: z.instanceof(File).optional(),
  block9_backgroundImage_ai_hint: z.string().optional().default("abstract background design"),
  block9_overlayColor: z.string().optional(),
  block9_impactPhrase: z.string().default('Vamos Construir Algo Incrível Juntos!'),
  block9_actionButton: z.object({
    text: z.string(),
    link: z.string(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
  }).default({ text: 'Comece Agora', link: '#contato' }),

  block_footer_isActive: z.boolean().optional().default(true),
  block_footer_order: z.number().optional().default(11),
  block_footer_logoUrl: z.string().optional().or(z.literal("")),
  block_footer_logoFile: z.instanceof(File).optional(),
  block_footer_additionalText: z.string().optional().default('<p>Sua Empresa Incrível Ltda.</p><p>CNPJ: 00.000.000/0000-00</p>'),
  block_footer_showSitemap: z.boolean().optional().default(true),
  block_footer_showAddress: z.boolean().optional().default(true),
  block_footer_showPhone: z.boolean().optional().default(true),
  block_footer_showWhatsapp: z.boolean().optional().default(true),
  block_footer_showEmail: z.boolean().optional().default(true),
  block_footer_showSocialLinks: z.boolean().optional().default(true),
  block_footer_showContactForm: z.boolean().optional().default(true),
  block_footer_showWhatsappButton: z.boolean().optional().default(true),
  block11_privacyPolicyLink: z.string().default('/politica-de-privacidade-generica'),
  privacyPolicyContent: z.string().optional().default('<h1>Política de Privacidade</h1><p>Este é um modelo de política de privacidade. Por favor, edite este conteúdo para refletir as práticas de privacidade do seu site. Consulte um profissional jurídico se necessário.</p>'),
  createdAt: z.custom<Timestamp | Date | string>((val) => val instanceof Timestamp || val instanceof Date || typeof val === 'string').optional().nullable(),
  updatedAt: z.custom<Timestamp | Date | string>((val) => val instanceof Timestamp || val instanceof Date || typeof val === 'string').optional().nullable(),
});

export type SiteData = z.infer<typeof SiteDataSchemaCore> & { id?: string };

// Schema for basic data collected from the user via chat.
export const AISiteCreationDataSchema = z.object({
  companyName: z.string().optional(),
  companyBranch: z.string().optional(),
  companyServicesProducts: z.string().optional(),
  companyDifferentiatorsInput: z.string().optional(),
  address: z.string().optional(),
  contactEmail: z.string().optional(),
  phone: z.string().optional(),
  whatsapp: z.string().optional(),
  whatsappDDI: z.string().optional(),
});
export type AISiteCreationData = z.infer<typeof AISiteCreationDataSchema>;

// Schema for creative content generated by AI based on the basic data.
// All fields are optional to make the generation process more robust.
export const GeneratedTextContentSchema = z.object({
  companySummary: z.string().optional(),
  block2_title: z.string().optional(),
  block2_subtitle: z.string().optional(),
  block_diff_items: z.array(z.object({
    id: z.string().optional(),
    icon: z.string(),
    title: z.string(),
    description: z.string(),
  })).optional(),
  block5_testimonials: z.array(z.object({
      id: z.string(),
      name: z.string(),
      title: z.string(),
      text: z.string()
  })).optional(),
  block3_text: z.string().optional(),
  block4_text: z.string().optional(),
  block9_impactPhrase: z.string().optional(),
  block8_faqs: z.array(z.object({
    id: z.string().optional(),
    question: z.string(),
    answer: z.string(),
  })).optional(),
  block2_backgroundImage_ai_hint: z.string().optional(),
  block3_photo_ai_hint: z.string().optional(),
  block4_photo_ai_hint: z.string().optional(),
  block9_backgroundImage_ai_hint: z.string().optional(),
  block_footer_logoUrl: z.string().optional(),
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  accentColor: z.string().optional(),
  block6_clientLogos: z.array(z.object({
    id: z.string().optional(),
    altText: z.string(),
  })).optional(),
  block7_galleryImages: z.array(z.object({
    id: z.string().optional(),
    altText: z.string(),
    imageHint: z.string().optional(),
  })).optional(),
}).partial();
export type GeneratedTextContent = z.infer<typeof GeneratedTextContentSchema>;

// Input/Output for the simple data gathering flow
export const GatherSiteInfoInputSchema = z.object({
  templateId: z.enum(['template1', 'template2', 'template3', 'template4']),
  conversationHistory: z.array(z.object({role: z.enum(['user', 'model']), content: z.string()})).optional(),
  extractedData: AISiteCreationDataSchema.partial().optional().describe("Dados básicos já coletados do cliente."),
});
export type GatherSiteInfoInput = z.infer<typeof GatherSiteInfoInputSchema>;

export const GatherSiteInfoOutputSchema = z.object({
  aiResponseToUser: z.string().describe("A resposta do assistente para o usuário."),
  extractedData: AISiteCreationDataSchema.partial().describe("Os dados básicos acumulados."),
  isGatheringComplete: z.boolean().describe("Definido como true SOMENTE quando a coleta de dados básicos terminar."),
});
export type GatherSiteInfoOutput = z.infer<typeof GatherSiteInfoOutputSchema>;

// Input/Output for Suggest Differentiators flow
export const SuggestDifferentiatorsInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  userInput: z.string().describe('O que o usuário digitou como diferenciais.'),
});
export type SuggestDifferentiatorsInput = z.infer<typeof SuggestDifferentiatorsInputSchema>;

export const SuggestDifferentiatorsOutputSchema = z.object({
  differentiators: z.array(z.object({
    id: z.string().optional(),
    icon: z.string().describe("Um nome de ícone da biblioteca Lucide. Ex: 'Star', 'Award', 'Zap'."),
    title: z.string(),
    description: z.string(),
  })).length(4).describe("Um array com exatamente 4 itens de diferenciais.")
});
export type SuggestDifferentiatorsOutput = z.infer<typeof SuggestDifferentiatorsOutputSchema>;

// Input/Output for Suggest FAQs flow
export const SuggestFaqsInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
});
export type SuggestFaqsInput = z.infer<typeof SuggestFaqsInputSchema>;

export const SuggestFaqsOutputSchema = z.object({
  faqs: z.array(z.object({
    id: z.string().optional(),
    question: z.string(),
    answer: z.string(),
  })).length(5).describe("Um array com exatamente 5 perguntas e respostas.")
});
export type SuggestFaqsOutput = z.infer<typeof SuggestFaqsOutputSchema>;

// Input/Output for Suggest Gallery Items flow
export const SuggestGalleryItemsInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companyBranch: z.string().describe('O ramo de atuação da empresa.'),
});
export type SuggestGalleryItemsInput = z.infer<typeof SuggestGalleryItemsInputSchema>;

export const SuggestGalleryItemsOutputSchema = z.object({
  galleryItems: z.array(z.object({
    id: z.string().optional(),
    altText: z.string().describe("Um texto alternativo descritivo para a imagem (em Português)."),
    imageHint: z.string().describe("Duas ou três palavras-chave em inglês para buscar a imagem."),
  })).length(6).describe("Um array com exatamente 6 itens para a galeria.")
});
export type SuggestGalleryItemsOutput = z.infer<typeof SuggestGalleryItemsOutputSchema>;

// Input/Output for Suggest Client Logos flow
export const SuggestClientLogosInputSchema = z.object({
    companyName: z.string().describe('O nome da empresa.'),
    companyBranch: z.string().describe('O ramo de atuação da empresa.'),
});
export type SuggestClientLogosInput = z.infer<typeof SuggestClientLogosInputSchema>;

export const SuggestClientLogosOutputSchema = z.object({
    clientLogos: z.array(z.object({
        id: z.string().optional(),
        altText: z.string().describe("O nome fictício de uma empresa cliente."),
    })).length(6).describe("Um array com exatamente 6 logos de clientes fictícios.")
});
export type SuggestClientLogosOutput = z.infer<typeof SuggestClientLogosOutputSchema>;

// Schemas para fluxos existentes (já definidos anteriormente)
// Apenas garantindo que estejam aqui para centralização

export const SuggestColorSchemeInputSchema = z.object({
  logoDataUri: z
    .string()
    .describe(
      "Opcional. Uma imagem de logo, como um data URI. Formato esperado: 'data:<mimetype>;base64,<dados_codificados>'."
    ).optional(),
  companyBranch: z.string().describe("O ramo de atuação da empresa. Ex: 'padaria', 'advocacia'.")
});
export type SuggestColorSchemeInput = z.infer<typeof SuggestColorSchemeInputSchema>;


export const SuggestColorSchemeOutputSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor primária inválida.").describe('A cor primária sugerida, como uma string hexadecimal (ex: #29ABE2).'),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor secundária inválida.").describe('A cor secundária sugerida, que é um tom mais claro ou análogo da cor primária.'),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor de destaque inválida.").describe('A cor de destaque sugerida, que é uma cor complementar ou vibrante que contrasta bem com a primária.')
});
export type SuggestColorSchemeOutput = z.infer<typeof SuggestColorSchemeOutputSchema>;


export const SuggestDifferentiatorItemDescriptionInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  itemTitle: z.string().describe('O título do item de diferencial para o qual gerar uma descrição (ex: "Qualidade Garantida").'),
});
export type SuggestDifferentiatorItemDescriptionInput = z.infer<typeof SuggestDifferentiatorItemDescriptionInputSchema>;

export const SuggestDifferentiatorItemDescriptionOutputSchema = z.object({
  suggestedText: z.string().describe('A descrição sugerida para o item de diferencial, concisa (apenas 1 frase).'),
});
export type SuggestDifferentiatorItemDescriptionOutput = z.infer<typeof SuggestDifferentiatorItemDescriptionOutputSchema>;


export const SuggestFaqAnswerInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  question: z.string().describe('A pergunta do FAQ para a qual gerar uma resposta.'),
});
export type SuggestFaqAnswerInput = z.infer<typeof SuggestFaqAnswerInputSchema>;

export const SuggestFaqAnswerOutputSchema = z.object({
  suggestedAnswer: z.string().describe('A resposta sugerida para a pergunta do FAQ, útil e informativa, com aproximadamente 1-2 frases. Pode usar HTML simples como <p> ou <strong> se necessário.'),
});
export type SuggestFaqAnswerOutput = z.infer<typeof SuggestFaqAnswerOutputSchema>;


export const SuggestFaqQuestionInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  existingQuestions: z.array(z.string()).optional().describe('Uma lista de perguntas que já existem na seção de FAQ.'),
});
export type SuggestFaqQuestionInput = z.infer<typeof SuggestFaqQuestionInputSchema>;

export const SuggestFaqQuestionOutputSchema = z.object({
  suggestedQuestion: z.string().describe('A pergunta sugerida para o FAQ.'),
});
export type SuggestFaqQuestionOutput = z.infer<typeof SuggestFaqQuestionOutputSchema>;


export const SuggestHeroTextInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  targetField: z.enum(['title', 'subtitle']).describe('O campo alvo para gerar o texto: "title" para o título principal ou "subtitle" para o subtítulo.'),
});
export type SuggestHeroTextInput = z.infer<typeof SuggestHeroTextInputSchema>;

export const SuggestHeroTextOutputSchema = z.object({
  suggestedText: z.string().describe('O texto sugerido para o campo alvo (título ou subtítulo).'),
});
export type SuggestHeroTextOutput = z.infer<typeof SuggestHeroTextOutputSchema>;


export const SuggestImageHintsInputSchema = z.object({
  companyBranch: z.string().describe('O ramo de atuação da empresa.'),
});
export type SuggestImageHintsInput = z.infer<typeof SuggestImageHintsInputSchema>;

export const SuggestImageHintsOutputSchema = z.object({
  heroHint: z.string().describe("Sugestão para a imagem principal (hero)."),
  aboutHint: z.string().describe("Sugestão para a imagem da seção 'Sobre Nós'."),
  servicesHint: z.string().describe("Sugestão para a imagem da seção de serviços."),
  ctaHint: z.string().describe("Sugestão para a imagem de fundo do banner CTA."),
});
export type SuggestImageHintsOutput = z.infer<typeof SuggestImageHintsOutputSchema>;


export const SuggestImpactPhraseInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
});
export type SuggestImpactPhraseInput = z.infer<typeof SuggestImpactPhraseInputSchema>;

export const SuggestImpactPhraseOutputSchema = z.object({
  suggestedText: z.string().describe('A frase de impacto sugerida para o banner CTA.'),
});
export type SuggestImpactPhraseOutput = z.infer<typeof SuggestImpactPhraseOutputSchema>;


export const SuggestSectionTextInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companySummary: z.string().describe('O resumo/descrição da empresa.'),
  sectionTitle: z.string().describe('O título da seção para a qual o texto será gerado (ex: "Sobre Nós", "Nossos Serviços").'),
});
export type SuggestSectionTextInput = z.infer<typeof SuggestSectionTextInputSchema>;

export const SuggestSectionTextOutputSchema = z.object({
  suggestedText: z.string().describe('O texto sugerido para a seção, com aproximadamente 2 parágrafos, formatado com tags <p>.'),
});
export type SuggestSectionTextOutput = z.infer<typeof SuggestSectionTextOutputSchema>;


export const SuggestTestimonialsInputSchema = z.object({
  companyName: z.string().describe('O nome da empresa.'),
  companyBranch: z.string().describe('O ramo de atuação da empresa.'),
  companyServicesProducts: z.string().describe('Os serviços ou produtos oferecidos.'),
});
export type SuggestTestimonialsInput = z.infer<typeof SuggestTestimonialsInputSchema>;

export const SuggestTestimonialsOutputSchema = z.object({
  testimonials: z.array(z.object({
    id: z.string().optional(),
    name: z.string(),
    title: z.string(),
    text: z.string(),
  })).length(4).describe("Um array com exatamente 4 depoimentos de clientes fictícios.")
});
export type SuggestTestimonialsOutput = z.infer<typeof SuggestTestimonialsOutputSchema>;

export const ParseAddressInputSchema = z.object({
    fullAddress: z.string().describe('A string de endereço completa fornecida pelo usuário.'),
});
export type ParseAddressInput = z.infer<typeof ParseAddressInputSchema>;

export const ParsedAddressOutputSchema = z.object({
    address_street: z.string().optional(),
    address_number: z.string().optional(),
    address_complement: z.string().optional(),
    address_neighborhood: z.string().optional(),
    address_city: z.string().optional(),
    address_state: z.string().optional(),
    address_zipCode: z.string().optional(),
});
export type ParsedAddressOutput = z.infer<typeof ParsedAddressOutputSchema>;

export const GenerateImageInputSchema = z.object({
  prompt: z.string().describe("O prompt de texto para gerar a imagem."),
});
export type GenerateImageInput = z.infer<typeof GenerateImageInputSchema>;

export const GenerateImageOutputSchema = z.object({
  imageDataUri: z.string().describe("A imagem gerada, como uma string data URI, incluindo o tipo MIME e codificação Base64."),
});
export type GenerateImageOutput = z.infer<typeof GenerateImageOutputSchema>;

export const FooterContactFormSchema = z.object({
  name: z.string().min(2, "Nome é muito curto."),
  email: z.string().email("Endereço de e-mail inválido."),
  phone: z.string().optional(),
  subject: z.string().min(3, "Assunto muito curto."),
  message: z.string().min(10, "A mensagem precisa ter pelo menos 10 caracteres."),
  honeypot: z.string().optional(),
});
export type FooterContactFormData = z.infer<typeof FooterContactFormSchema>;

export const ReportAbuseFormDataSchema = z.object({
  reporterName: z.string().min(2, "Seu nome é obrigatório."),
  reporterEmail: z.string().email("Seu e-mail é obrigatório e precisa ser válido."),
  reporterPhone: z.string().optional(),
  reportMessage: z.string().min(20, "Por favor, forneça uma descrição detalhada da sua denúncia."),
  reportedSiteSubdomain: z.string(),
  reportedSiteCustomDomain: z.string().optional(),
  honeypot: z.string().optional(),
});
export type ReportAbuseFormData = z.infer<typeof ReportAbuseFormDataSchema>;

export const SmtpFormDataSchema = z.object({
  smtpHost: z.string().min(1, 'Host SMTP é obrigatório'),
  smtpPort: z.number().int().min(1, 'Porta SMTP é obrigatória'),
  smtpUser: z.string().min(1, 'Usuário SMTP é obrigatório'),
  smtpPass: z.string().optional(),
  smtpSecure: z.boolean(),
  emailFromAddress: z.string().email('Email remetente inválido'),
});
export type SmtpFormData = z.infer<typeof SmtpFormDataSchema>;

export const TestEmailFormDataSchema = z.object({
  recipientEmail: z.string().email('Email do destinatário inválido'),
  subject: z.string().min(1, 'Assunto é obrigatório'),
  messageBody: z.string().min(1, 'Mensagem é obrigatória'),
});
export type TestEmailFormData = z.infer<typeof TestEmailFormDataSchema>;

export const PayPalPlansFormDataSchema = z.object({
  productId: z.string().optional(),
  planIds: z.record(planIdEnum, z.string().optional()),
  prices: z.record(planIdEnum, z.number().positive()),
});
export type PayPalPlansFormData = z.infer<typeof PayPalPlansFormDataSchema>;

export type UserCreationEligibility = {
  canCreate: boolean;
  reason: 'FIRST_SITE' | 'NO_FREE_TRIAL_LEFT' | 'ALREADY_HAS_PAID_PLAN' | 'UNKNOWN';
  message: string;
};

export const CouponSchema = z.object({
  id: z.string().optional(),
  code: z.string().min(3, "Código muito curto").max(50, "Código muito longo").regex(/^[A-Z0-9_-]+$/, "Código pode conter apenas letras maiúsculas, números, - e _"),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number().positive("Valor do desconto deve ser positivo"),
  expiresAt: z.date().nullable().optional(),
  maxUses: z.number().int().positive("Número máximo de usos deve ser positivo").nullable().optional(),
  uses: z.number().int().min(0).optional().default(0),
  isActive: z.boolean().optional().default(true),
  createdAt: z.custom<Timestamp | Date | string>().optional(),
  updatedAt: z.custom<Timestamp | Date | string>().optional(),
});
export type Coupon = z.infer<typeof CouponSchema>;

export interface Subscription {
  id?: string;
  userId: string;
  planId: PlanId;
  planName?: string; // Adicionado para facilitar a exibição
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'expired_trial' | 'expired' | 'suspended';
  paypalSubscriptionId?: string;
  startDate?: string | Date | Timestamp;
  endDate?: string | Date | Timestamp;
  createdAt?: string | Date | Timestamp;
  updatedAt?: string | Date | Timestamp;
  siteId?: string | null;
}

export interface UserDocument {
  email: string;
  displayName: string;
  createdAt: string | Date | Timestamp;
  updatedAt?: string | Date | Timestamp;
  activeSubscriptionId?: string;
  activeCoupon?: Pick<Coupon, 'id' | 'code' | 'discountType' | 'discountValue' | 'expiresAt'>;
  adminNotes?: string;
  deletionScheduledAt?: Timestamp | null;
}

export type DailyChartData = {
  date: string;
  count?: number;
  amount?: number;
};

export type AdminDashboardStats = {
    totalClients: number;
    activeSites: number;
    delinquentCount: number;
    totalRevenue: number;
    monthlyRecurringRevenue: number; // MRR
    dailyUserSignups: DailyChartData[];
    dailySiteCreations: DailyChartData[];
    dailyRevenue: DailyChartData[];
};

export type AggregatedAnalytics = {
  totalSites: number;
  totalViews: number;
  availableSlots: number;
  dailyData: { date: string; visits: number }[];
  breakdownData: { name: string; views: number }[];
};

export const PolicyTermsSchema = z.object({
  defaultSitePrivacyPolicy: z.string().min(10, "O conteúdo da Política de Privacidade padrão do site é muito curto."),
  appTermsOfService: z.string().min(10, "O conteúdo dos Termos de Serviço da plataforma é muito curto."),
  appPrivacyPolicy: z.string().min(10, "O conteúdo da Política de Privacidade da plataforma é muito curto."),
  updatedAt: z.custom<Timestamp | Date | string>().optional(),
});
export type PolicyTermsFormData = z.infer<typeof PolicyTermsSchema>;


export const EmailTemplatesFormDataSchema = z.object({
  sitePublishedSubject: z.string().min(5, "Assunto muito curto"),
  sitePublishedBody: z.string().min(20, "Corpo do e-mail muito curto"),
  trialExpiringSoonSubject: z.string().min(5, "Assunto muito curto"),
  trialExpiringSoonBody: z.string().min(20, "Corpo do e-mail muito curto"),
  trialLastDaySubject: z.string().min(5, "Assunto muito curto"),
  trialLastDayBody: z.string().min(20, "Corpo do e-mail muito curto"),
  updatedAt: z.custom<Timestamp | Date | string>().optional(),
});
export type EmailTemplatesFormData = z.infer<typeof EmailTemplatesFormDataSchema>;


export const AppearanceSettingsFormDataSchema = z.object({
  globalPrimaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Formato de cor inválido. Use #RRGGBB.").optional(),
  globalSecondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Formato de cor inválido. Use #RRGGBB.").optional(),
  globalAccentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Formato de cor inválido. Use #RRGGBB.").optional(),
  marketingPageHeroTitle: z.string().min(1, "O título principal é obrigatório."),
  marketingPageHeroSubtitle: z.string().min(1, "O subtítulo é obrigatório."),
  // Vídeo para a página de Conteúdo
  onboardingVideoUrl: z.string().url("A URL do vídeo é inválida.").optional().or(z.literal('')),
  onboardingVideoAutoplay: z.boolean().optional().default(false),
  // Vídeo para a página de Configurações
  editSiteOnboardingVideoUrl: z.string().url("A URL do vídeo é inválida.").optional().or(z.literal('')),
  editSiteOnboardingVideoAutoplay: z.boolean().optional().default(false),
  updatedAt: z.custom<Timestamp | Date | string>().optional(),
});
export type AppearanceSettingsFormData = z.infer<typeof AppearanceSettingsFormDataSchema>;

export interface UnsplashPhoto {
  id: string;
  alt_description: string | null;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  links: {
    html: string;
    download_location: string;
  };
  user: {
    name: string;
    links: {
      html: string;
    };
  };
}
